/**
 * Branding Configuration - Seasonal Logo Switching
 * 
 * Automatically displays seasonal logos based on date ranges.
 * Falls back to default logo outside seasonal windows.
 */

// Seasonal logo configuration
export const BRANDING_CONFIG = {
  defaultLogoUrl: '/brand/seasonal/stock-load-default.png',
  
  // Seasonal logos with date ranges (MM-DD format, inclusive)
  // Priority: higher number wins if multiple windows overlap
  seasonalLogos: [
    {
      name: 'valentines-day',
      logoUrl: '/brand/seasonal/stock-load-valentines-day.png',
      startMmDd: '02-01',
      endMmDd: '02-14',
      priority: 10
    },
    {
      name: 'st-patricks-day',
      logoUrl: '/brand/seasonal/stock-load-st-patricks-day.png',
      startMmDd: '03-01',
      endMmDd: '03-17',
      priority: 10
    },
    {
      name: 'easter',
      logoUrl: '/brand/seasonal/stock-load-easter.png',
      startMmDd: '03-15',
      endMmDd: '04-15',
      priority: 5  // Lower priority - St Patrick's wins if overlap
    },
    {
      name: 'memorial-day',
      logoUrl: '/brand/seasonal/stock-load-memorial-day.png',
      startMmDd: '05-15',
      endMmDd: '05-31',
      priority: 10
    },
    {
      name: '4th-of-july',
      logoUrl: '/brand/seasonal/stock-load-4th-of-july.png',
      startMmDd: '06-24',
      endMmDd: '07-07',
      priority: 10
    },
    {
      name: 'halloween',
      logoUrl: '/brand/seasonal/stock-load-halloween.png',
      startMmDd: '10-01',
      endMmDd: '10-31',
      priority: 10
    },
    {
      name: 'thanksgiving',
      logoUrl: '/brand/seasonal/stock-load-thanksgiving.png',
      startMmDd: '11-20',
      endMmDd: '11-30',
      priority: 15
    },
    {
      name: 'christmas',
      logoUrl: '/brand/seasonal/stock-load-christmas.png',
      startMmDd: '11-20',
      endMmDd: '12-26',
      priority: 10
    }
  ],
  
  // Admin override settings (can be stored in localStorage for now)
  forcedLogoEnabled: false,
  forcedLogoUrl: null
};

// Cache keys
const CACHE_KEY = 'branding_config';
const FORCE_LOGO_KEY = 'branding_force_logo';
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Parse MM-DD string to { month, day }
 */
const parseMmDd = (mmDd) => {
  const [month, day] = mmDd.split('-').map(Number);
  return { month, day };
};

/**
 * Check if a date (month/day) is within a range
 * Handles year wrap-around (e.g., Nov 20 - Jan 5)
 */
const isDateInRange = (month, day, startMmDd, endMmDd) => {
  const start = parseMmDd(startMmDd);
  const end = parseMmDd(endMmDd);
  
  // Create comparable day-of-year values (month * 100 + day)
  const current = month * 100 + day;
  const startVal = start.month * 100 + start.day;
  const endVal = end.month * 100 + end.day;
  
  // Handle year wrap-around (e.g., Dec 20 to Jan 5)
  if (startVal > endVal) {
    // Range wraps around year end
    return current >= startVal || current <= endVal;
  }
  
  // Normal range within same year
  return current >= startVal && current <= endVal;
};

/**
 * Get the active logo based on current date and config
 * 
 * @param {Object} config - Branding configuration
 * @param {Date} now - Current date (defaults to now)
 * @param {string} timezone - IANA timezone (optional, defaults to local)
 * @returns {Object} { logoUrl, seasonName, isForced }
 */
export const getActiveLogo = (config = BRANDING_CONFIG, now = new Date(), timezone = null) => {
  // Check for forced logo override first
  const forceOverride = getForceLogoOverride();
  if (forceOverride.enabled && forceOverride.logoUrl) {
    return {
      logoUrl: forceOverride.logoUrl,
      seasonName: 'forced',
      isForced: true
    };
  }
  
  // Get current month/day in the specified timezone
  let month, day;
  
  if (timezone) {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        month: 'numeric',
        day: 'numeric'
      });
      const parts = formatter.formatToParts(now);
      month = parseInt(parts.find(p => p.type === 'month')?.value || '0', 10);
      day = parseInt(parts.find(p => p.type === 'day')?.value || '0', 10);
    } catch (e) {
      // Fallback to local time if timezone is invalid
      month = now.getMonth() + 1;
      day = now.getDate();
    }
  } else {
    month = now.getMonth() + 1;
    day = now.getDate();
  }
  
  // Find matching seasonal logos
  const matchingLogos = config.seasonalLogos.filter(logo => 
    isDateInRange(month, day, logo.startMmDd, logo.endMmDd)
  );
  
  if (matchingLogos.length === 0) {
    return {
      logoUrl: config.defaultLogoUrl,
      seasonName: 'default',
      isForced: false
    };
  }
  
  // Sort by priority (highest first) and return top match
  matchingLogos.sort((a, b) => b.priority - a.priority);
  const activeLogo = matchingLogos[0];
  
  return {
    logoUrl: activeLogo.logoUrl,
    seasonName: activeLogo.name,
    isForced: false
  };
};

/**
 * Get force logo override from localStorage
 */
export const getForceLogoOverride = () => {
  try {
    const stored = localStorage.getItem(FORCE_LOGO_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // Ignore
  }
  return { enabled: false, logoUrl: null };
};

/**
 * Set force logo override (admin only)
 */
export const setForceLogoOverride = (enabled, logoUrl = null) => {
  try {
    localStorage.setItem(FORCE_LOGO_KEY, JSON.stringify({ enabled, logoUrl }));
  } catch (e) {
    console.warn('Failed to save force logo override:', e);
  }
};

/**
 * Clear force logo override
 */
export const clearForceLogoOverride = () => {
  try {
    localStorage.removeItem(FORCE_LOGO_KEY);
  } catch (e) {
    // Ignore
  }
};

/**
 * Get all available logos for admin selection
 */
export const getAvailableLogos = () => {
  return [
    { name: 'Default', url: BRANDING_CONFIG.defaultLogoUrl },
    ...BRANDING_CONFIG.seasonalLogos.map(logo => ({
      name: logo.name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
      url: logo.logoUrl
    }))
  ];
};

/**
 * Hook-friendly wrapper to get current logo URL
 * Uses the auth context's tenant timezone if available
 */
export const getCurrentLogoUrl = (tenantTimezone = null) => {
  const result = getActiveLogo(BRANDING_CONFIG, new Date(), tenantTimezone);
  return result.logoUrl;
};
